// ════════════════════════════════════════════════════════════════════════════
// BotonDictado — dictado de voz para campos de nota (prompt 0759 · vivo 0761)
// Camino 1 (Chrome/Edge): Web Speech API con interimResults → el texto se escribe
//   EN VIVO en la nota mientras se habla (efecto máquina de escribir).
// Camino 2 (Safari/Firefox sin soporte): fallback a MediaRecorder + POST /via/transcribir
//   (Whisper batch, prompt 0759). El audio no se persiste; solo el texto.
// Contrato: value + onChange (actualización viva) + onCommit opcional (una vez al final,
//   para consumidores que persisten en vivo, p.ej. PEMM).
// ════════════════════════════════════════════════════════════════════════════
import { useState, useRef, useCallback, type CSSProperties } from 'react';
import { useI18n } from '../../../i18n';
import { transcribirAudio } from '../services/api';

const A2 = '#2F5496';
const RED = '#C0392B';
const GO = '#595959';
const GM = '#D9D9D9';
const W = '#FFF';

type Estado = 'idle' | 'activo' | 'procesando';

interface Props {
  /** Valor actual de la nota; el dictado escribe sobre él en vivo. */
  value: string;
  /** Actualización viva del texto (interino + final). */
  onChange: (v: string) => void;
  /** Se invoca UNA vez al finalizar, con el valor final (consumidores que persisten). */
  onCommit?: (v: string) => void;
  maxLength?: number;
  disabled?: boolean;
}

// ── Web Speech API — tipado mínimo (no está en el lib DOM por defecto) ──
interface SRResult {
  isFinal: boolean;
  0: { transcript: string };
}
interface SREvent {
  resultIndex: number;
  results: { length: number; [i: number]: SRResult };
}
interface SRecognition {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start(): void;
  stop(): void;
  onresult: ((e: SREvent) => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  onend: (() => void) | null;
}
type SRCtor = new () => SRecognition;

function getSR(): SRCtor | null {
  const w = window as unknown as { SpeechRecognition?: SRCtor; webkitSpeechRecognition?: SRCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}
const SR_LANG: Record<string, string> = { es: 'es-ES', en: 'en-US', zh: 'zh-CN' };

export function BotonDictado({ value, onChange, onCommit, maxLength = 1000, disabled }: Props) {
  const { t, lang } = useI18n();
  const d = t.common.dictado;
  const [estado, setEstado] = useState<Estado>('idle');
  const [error, setError] = useState<string | null>(null);
  const srRef = useRef<SRecognition | null>(null);
  const mediaRef = useRef<MediaRecorder | null>(null);
  const modoRef = useRef<'live' | 'rec' | null>(null);
  const baseRef = useRef('');
  const finalRef = useRef('');
  const chunksRef = useRef<Blob[]>([]);

  const compose = useCallback(
    (base: string, add: string) => {
      const b = base.trimEnd();
      const joined = b ? (add ? `${b} ${add}` : b) : add;
      return joined.slice(0, maxLength);
    },
    [maxLength],
  );

  // ── Camino 1: Web Speech API (dictado EN VIVO) ──
  const iniciarLive = useCallback(
    (Ctor: SRCtor) => {
      const rec = new Ctor();
      rec.lang = SR_LANG[lang] ?? 'es-ES';
      rec.interimResults = true;
      rec.continuous = true;
      baseRef.current = value;
      finalRef.current = '';
      rec.onresult = (e) => {
        let interim = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const txt = e.results[i][0].transcript;
          if (e.results[i].isFinal) finalRef.current += txt;
          else interim += txt;
        }
        onChange(compose(baseRef.current, `${finalRef.current}${interim}`.trim()));
      };
      rec.onerror = (ev) => {
        if (ev.error === 'no-speech' || ev.error === 'aborted') return;
        setError(ev.error === 'not-allowed' ? d.sinPermiso : d.error);
      };
      rec.onend = () => {
        const finalVal = compose(baseRef.current, finalRef.current.trim());
        onChange(finalVal);
        onCommit?.(finalVal);
        setEstado('idle');
        srRef.current = null;
        modoRef.current = null;
      };
      srRef.current = rec;
      modoRef.current = 'live';
      setError(null);
      rec.start();
      setEstado('activo');
    },
    [lang, value, onChange, onCommit, compose, d],
  );

  // ── Camino 2: fallback Whisper (grabar → transcribir → añadir) ──
  const iniciarFallback = useCallback(async () => {
    setError(null);
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setError(d.noSoportado);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      baseRef.current = value;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const tipo = rec.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: tipo });
        modoRef.current = null;
        if (blob.size === 0) {
          setEstado('idle');
          return;
        }
        setEstado('procesando');
        try {
          const ext = tipo.includes('mp4') ? 'mp4' : 'webm';
          const res = await transcribirAudio(blob, `nota.${ext}`, lang);
          if (res.texto) {
            const finalVal = compose(baseRef.current, res.texto.trim());
            onChange(finalVal);
            onCommit?.(finalVal);
          }
        } catch (err) {
          setError((err as Error).message || d.error);
        } finally {
          setEstado('idle');
        }
      };
      mediaRef.current = rec;
      modoRef.current = 'rec';
      rec.start();
      setEstado('activo');
    } catch {
      setError(d.sinPermiso);
    }
  }, [lang, value, onChange, onCommit, compose, d]);

  const detener = useCallback(() => {
    if (modoRef.current === 'live') srRef.current?.stop();
    else if (modoRef.current === 'rec') mediaRef.current?.stop();
  }, []);

  const iniciar = useCallback(() => {
    const Ctor = getSR();
    if (Ctor) iniciarLive(Ctor);
    else void iniciarFallback();
  }, [iniciarLive, iniciarFallback]);

  const onClick = estado === 'activo' ? detener : estado === 'idle' ? iniciar : undefined;
  const activo = estado === 'activo';

  const btnStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    marginTop: 4,
    fontSize: 10,
    fontWeight: 700,
    padding: '3px 8px',
    borderRadius: 5,
    border: `1px solid ${activo ? RED : GM}`,
    background: activo ? '#FCEBEA' : W,
    color: activo ? RED : A2,
    cursor: disabled || estado === 'procesando' ? 'default' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    fontFamily: "'DM Sans',sans-serif",
  };

  return (
    <div>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled || estado === 'procesando'}
        title={activo ? d.detener : d.dictar}
        aria-label={activo ? d.detener : d.dictar}
        style={btnStyle}
      >
        {estado === 'procesando' ? <Spinner /> : activo ? <IconStop /> : <IconMic />}
        <span>{estado === 'procesando' ? d.transcribiendo : activo ? d.detener : d.dictar}</span>
      </button>
      {error && (
        <div style={{ color: '#7B0000', fontSize: 10, marginTop: 2 }} role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

function IconMic() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="9" y="2" width="6" height="12" rx="3" fill={A2} />
      <path
        d="M5 11a7 7 0 0 0 14 0M12 18v3"
        stroke={A2}
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function IconStop() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="6" y="6" width="12" height="12" rx="2" fill={RED} />
    </svg>
  );
}

function Spinner() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke={GM} strokeWidth="3" fill="none" />
      <path d="M12 3a9 9 0 0 1 9 9" stroke={GO} strokeWidth="3" fill="none" strokeLinecap="round">
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 12 12"
          to="360 12 12"
          dur="0.8s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  );
}
